// <i80-drawer>: our own component (not in Helios), made for i80. A vertical panel that
// folds down inside the normal page flow — a sibling to <i80-flyout>, but inline rather
// than a modal viewport overlay. It is content-agnostic: the author drops a row of
// elements in, and those elements can be dragged out and dropped into another field.
//
// Two ways to open: in `push` mode the panel takes room in the flow and pushes the
// content below it down; in `float` mode it lies over that content without moving it.
//
// Like the flow, it only draws and reports. The items are the children the author (or the
// server) renders; a drop fires a cancellable event (i80-drawer-drop) the page can answer.
// If nothing cancels it and the item landed on an <i80-dropzone>, the component moves it in
// itself (or copies it, with `reusable`), so it also works as a standalone widget.
//
// Drag is Pointer Events + setPointerCapture + a cancellable CustomEvent, the house way
// (see flow.js); no HTML5 drag-and-drop. The drop target lives outside the component, so a
// pointerup hit-tests with document.elementsFromPoint and the event bubbles, letting any
// field on the page — the bundled <i80-dropzone>, or the author's own [data-i80-drop] —
// catch it.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";
import css from "./drawer.css";

const ITEM_SELECTOR = "i80-drawer-item, [data-i80-drag]";
const DROP_SELECTOR = "i80-dropzone, [data-i80-drop]";

function ensureStyles() {
  if (document.querySelector("style[data-i80-drawer]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-i80-drawer", "");
  style.textContent = css;
  document.head.append(style);
}

/** The item's type, for a dropzone's `accepts` filter. */
function itemType(item) {
  return item.getAttribute?.("type") ?? "";
}

/**
 * <i80-dropzone label="Valgte felter" empty-label="Træk felter hertil">
 *   <!-- items dropped here land as children -->
 * </i80-dropzone>
 *
 * A thin target that takes items dropped from an <i80-drawer>. Content-agnostic: whatever is
 * dropped becomes a child. Fires a cancellable i80-dropzone-drop; prevent it to keep the
 * markup yours. `accepts` (a space/comma list of item types) turns away items of other types.
 */
export class I80Dropzone extends I80Element {
  static get observedAttributes() {
    return ["label", "empty-label", "accepts"];
  }

  setup() {
    ensureStyles();
    let zone = this.part("zone");
    if (!zone) {
      zone = h("div", { class: "i80-dropzone", "data-i80-part": "zone", role: "group" }, ...this.takeContent());
      this.append(zone);
    }
    this._zone = zone;
    this._empty = null;
  }

  adopt() {
    this._zone.append(...this.takeContent());
  }

  update() {
    const label = this.getAttribute("label");
    if (label) this._zone.setAttribute("aria-label", label);
    else this._zone.removeAttribute("aria-label");
    this._zone.classList.toggle("i80-dropzone--empty", !this._hasItems());
    this._renderEmpty();
  }

  _hasItems() {
    return [...this._zone.children].some((c) => c !== this._empty);
  }

  _renderEmpty() {
    const text = this.getAttribute("empty-label");
    if (this._hasItems() || !text) {
      this._empty?.remove();
      this._empty = null;
      return;
    }
    if (!this._empty) {
      this._empty = h("p", { class: "i80-dropzone__empty", "aria-hidden": "true" });
      this._zone.append(this._empty);
    }
    this._empty.textContent = text;
  }

  /** The types this zone takes, or null for any. */
  get accepts() {
    const raw = this.getAttribute("accepts");
    if (!raw) return null;
    return raw.split(/[\s,]+/).filter(Boolean);
  }

  accepted(item) {
    const list = this.accepts;
    return !list || list.includes(itemType(item));
  }

  /** Show or clear the drop highlight (the drawer calls this during a drag). */
  highlight(on) {
    this._zone.classList.toggle("i80-dropzone--over", !!on);
  }

  /**
   * Take an item in: fire a cancellable i80-dropzone-drop, then (unless prevented or the type
   * is turned away) append it. Returns whether it landed.
   */
  accept(item, info = {}) {
    if (!this.accepted(item)) return false;
    const ok = this.dispatchEvent(new CustomEvent("i80-dropzone-drop", { bubbles: true, cancelable: true, detail: { item, from: info.from ?? null, type: itemType(item), value: item.getAttribute?.("value") ?? null } }));
    if (!ok) return false;
    this._empty?.remove();
    this._empty = null;
    this._zone.append(item);
    this.update();
    return true;
  }
}

/**
 * <i80-drawer-item type="field" value="name">Navn</i80-drawer-item>
 *
 * A draggable unit inside a drawer. Content-agnostic — its text (or aria-label) names it.
 * The parent drawer runs the pointer and keyboard gestures; the item only has to be a
 * focusable, grabbable box, so it carries no listeners of its own.
 */
export class I80DrawerItem extends I80Element {
  static get observedAttributes() {
    return ["type", "value", "disabled"];
  }

  setup() {
    this.classList.add("i80-drawer__item");
  }

  update() {
    this.setAttribute("role", "button");
    this.classList.toggle("i80-drawer__item--disabled", this.flag("disabled"));
    this.setAttribute("tabindex", this.flag("disabled") ? "-1" : "0");
    if (this.flag("disabled")) this.setAttribute("aria-disabled", "true");
    else this.removeAttribute("aria-disabled");
    if (!this.getAttribute("aria-label")) {
      const text = this.textContent.trim();
      if (text) this.setAttribute("aria-label", text);
    }
  }
}

/**
 * <i80-drawer label="Felter" mode="push">
 *   <i80-drawer-item type="field" value="name">Navn</i80-drawer-item>
 *   <i80-drawer-item type="field" value="email">E-mail</i80-drawer-item>
 * </i80-drawer>
 * <i80-dropzone label="Valgte" empty-label="Træk felter hertil"></i80-dropzone>
 */
export class I80Drawer extends I80Element {
  static get observedAttributes() {
    return ["open", "mode", "label", "dismiss-label", "reusable"];
  }

  setup() {
    ensureStyles();
    let root = this.part("drawer");
    if (!root) {
      const panelId = nextId("i80-drawer-panel");
      const items = h("div", { class: "i80-drawer__items", "data-i80-part": "items", role: "group" }, ...this.takeContent());
      const panel = h("div", { class: "i80-drawer__panel", "data-i80-part": "panel", id: panelId, role: "group" }, items);
      const wrapper = h("div", { class: "i80-drawer__wrapper", "data-i80-part": "wrapper" }, panel);
      const toggle = h("button", { type: "button", class: "i80-drawer__toggle", "data-i80-part": "toggle", "aria-controls": panelId });
      root = h("div", { class: "i80-drawer", "data-i80-part": "drawer" }, toggle, wrapper);
      this.append(root);
    }
    this._root = root;
    this._toggle = root.querySelector('[data-i80-part="toggle"]');
    this._wrapper = root.querySelector('[data-i80-part="wrapper"]');
    this._panel = root.querySelector('[data-i80-part="panel"]');
    this._items = root.querySelector('[data-i80-part="items"]');
    // A clone carries the original id; give each instance its own.
    const id = nextId("i80-drawer-panel");
    this._panel.id = id;
    this._toggle.setAttribute("aria-controls", id);

    this._toggle.addEventListener("click", () => this.toggle());
    // One set of listeners on the items box covers every item: pointer capture on the item
    // keeps the move/up events coming here for the whole drag (see flow.js).
    this._items.addEventListener("pointerdown", (e) => this._down(e));
    this._items.addEventListener("pointermove", (e) => this._move(e));
    this._items.addEventListener("pointerup", (e) => this._up(e));
    this._items.addEventListener("pointercancel", () => this._cancelDrag());
    this._items.addEventListener("click", (e) => this._click(e));
    this._items.addEventListener("keydown", (e) => this._key(e));
  }

  adopt() {
    this._items.append(...this.takeContent());
  }

  update() {
    const open = this.flag("open");
    const mode = this.choice("mode", ["push", "float"], "push");
    this._root.classList.toggle("i80-drawer--open", open);
    this._root.classList.toggle("i80-drawer--push", mode === "push");
    this._root.classList.toggle("i80-drawer--float", mode === "float");

    const label = this.getAttribute("label");
    this._toggle.hidden = !label;
    if (label) {
      this._root.classList.add("i80-drawer--labelled");
      this._toggle.setAttribute("aria-expanded", String(open));
      this._toggle.replaceChildren(
        h("span", { class: "i80-drawer__toggle-icon" }, icon("chevron-down", { stretched: true })),
        h("span", { class: "i80-drawer__toggle-text" }, label));
    } else {
      this._root.classList.remove("i80-drawer--labelled");
    }

    // When closed the panel is out of the flow of focus and the reading order, even while it
    // animates shut — inert keeps its focusable items off the keyboard and hides them from AT
    // (so no redundant aria-hidden, which would trip the aria-hidden-focus check).
    this._panel.inert = !open;
  }

  get reusable() {
    return this.flag("reusable");
  }

  get open() {
    return this.flag("open");
  }

  set open(value) {
    this.toggleAttribute("open", !!value);
  }

  show() {
    if (this.flag("open")) return;
    this.setAttribute("open", "");
    this.dispatchEvent(new CustomEvent("i80-drawer-open", { bubbles: true }));
  }

  close() {
    if (!this.flag("open")) return;
    const ok = this.dispatchEvent(new CustomEvent("i80-drawer-close", { bubbles: true, cancelable: true }));
    if (!ok) return;
    this.removeAttribute("open");
  }

  toggle(force) {
    const open = force ?? !this.flag("open");
    if (open) this.show();
    else this.close();
  }

  // --- drag: pick an item up, follow the pointer, drop it on a target ---

  _item(target) {
    const el = target.closest?.(ITEM_SELECTOR);
    return el && this._items.contains(el) && !el.matches(".i80-drawer__item--disabled") && el.getAttribute("aria-disabled") !== "true" ? el : null;
  }

  _down(e) {
    if (e.button !== 0) return;
    const item = this._item(e.target);
    if (!item) return;
    this._drag = { item, x0: e.clientX, y0: e.clientY, moved: false, over: null };
    // Capture on the item, not the container: a capture on a container retargets the click
    // that follows to it, and the keyboard/activate click would be lost (see flow.js).
    item.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }

  _move(e) {
    const d = this._drag;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 4) return;
    d.moved = true;
    d.item.classList.add("i80-drawer__item--dragging");
    this._ghost(e, d);
    const target = this._targetUnder(e, d.item);
    if (target !== d.over) {
      this._setOver(d.over, false);
      this._setOver(target, true);
      d.over = target;
    }
  }

  _ghost(e, d) {
    if (!d.ghost) {
      d.ghost = d.item.cloneNode(true);
      d.ghost.classList.add("i80-drawer__ghost");
      d.ghost.removeAttribute("id");
      d.ghost.style.width = `${d.item.offsetWidth}px`;
      document.body.append(d.ghost);
    }
    d.ghost.style.left = `${e.clientX + 10}px`;
    d.ghost.style.top = `${e.clientY + 10}px`;
  }

  /** The drop target under the pointer, if any — skipping our own ghost. */
  _targetUnder(e, item) {
    for (const el of document.elementsFromPoint(e.clientX, e.clientY)) {
      if (el.closest(".i80-drawer__ghost")) continue;
      const target = el.closest?.(DROP_SELECTOR);
      if (target && !item.contains(target)) return target;
    }
    return null;
  }

  _setOver(target, on) {
    if (!target) return;
    if (typeof target.highlight === "function") target.highlight(on);
    else target.toggleAttribute("data-i80-over", on);
  }

  _up(e) {
    const d = this._drag;
    if (!d) return;
    this._drag = null;
    d.ghost?.remove();
    d.item.classList.remove("i80-drawer__item--dragging");
    this._setOver(d.over, false);
    if (!d.moved) return; // a plain click/keypress is handled elsewhere
    this._suppress();
    this._drop(d.item, d.over, e.clientX, e.clientY);
  }

  /** Report the drop; unless the page answers it, move (or copy) the item into the dropzone. */
  _drop(item, target, clientX, clientY) {
    const dropzone = target && typeof target.accept === "function" ? target : null;
    const ok = this.dispatchEvent(new CustomEvent("i80-drawer-drop", {
      bubbles: true, cancelable: true,
      detail: { item, from: this, target: target ?? null, dropzone, type: itemType(item), value: item.getAttribute?.("value") ?? null, clientX, clientY },
    }));
    if (!ok || !dropzone) return;
    this._place(item, dropzone);
  }

  /** The built-in landing: a copy when `reusable` (the drawer keeps the original), else a move. */
  _place(item, dropzone) {
    if (!dropzone.accepted(item)) return false;
    const node = this.reusable ? item.cloneNode(true) : item;
    if (this.reusable) node.removeAttribute("id");
    return dropzone.accept(node, { from: this });
  }

  /** Swallow the click a pointerup synthesises after a real drag. */
  _suppress() {
    this._suppressClick = true;
    setTimeout(() => (this._suppressClick = false));
  }

  _cancelDrag() {
    if (!this._drag) return;
    this._drag.ghost?.remove();
    this._drag.item.classList.remove("i80-drawer__item--dragging");
    this._setOver(this._drag.over, false);
    this._drag = null;
  }

  _click(e) {
    if (this._suppressClick) return;
    const item = this._item(e.target);
    if (item) this._activate(item);
  }

  _key(e) {
    if (e.key !== "Enter" && e.key !== " ") return;
    const item = this._item(e.target);
    if (!item) return;
    e.preventDefault();
    this._activate(item);
  }

  /**
   * The keyboard (and plain-click) path: report i80-drawer-item-activate, then, unless the
   * page answers it, send the item to this drawer's default dropzone — so a drawer is usable
   * without a pointer, the way a flow block adds on click.
   */
  _activate(item) {
    const ok = this.dispatchEvent(new CustomEvent("i80-drawer-item-activate", {
      bubbles: true, cancelable: true,
      detail: { item, from: this, type: itemType(item), value: item.getAttribute("value") ?? null },
    }));
    if (!ok) return;
    const dropzone = this._defaultDropzone(item);
    if (dropzone) this._place(item, dropzone);
  }

  /**
   * Where a click or keypress sends an item, when the page doesn't answer: the dropzone named
   * by the `dropzone` attribute (an id, like aria-controls), or — only when there is no
   * ambiguity — the single dropzone on the page. With several dropzones and no `dropzone`
   * attribute it returns none, so the item stays put rather than landing in the wrong field
   * (a pointer drag is unaffected; it drops on whatever is actually under it).
   */
  _defaultDropzone(item) {
    const id = this.getAttribute("dropzone");
    if (id) {
      const el = document.getElementById(id);
      return el && typeof el.accept === "function" && el.accepted(item) ? el : null;
    }
    const zones = [...document.querySelectorAll("i80-dropzone")].filter((z) => typeof z.accept === "function" && z.accepted(item));
    return zones.length === 1 ? zones[0] : null;
  }
}

// Order matters: the item and the dropzone are looked up by the drawer, so register them
// first (see the define() note in base.js).
define("i80-drawer-item", I80DrawerItem);
define("i80-dropzone", I80Dropzone);
define("i80-drawer", I80Drawer);
